// components/books/BookFormControls.tsx
import {useRef, useState} from "react";
import {Plus, Check, Upload} from "lucide-react";
import {
    Box, Button, Center, FormControl, FormErrorMessage, FormLabel,
    Icon, Image, Input, Menu, MenuButton, MenuItemOption, MenuList,
    MenuOptionGroup, Spinner, Tag, TagCloseButton, TagLabel, Text,
    VStack, Wrap, WrapItem,
} from "@chakra-ui/react";

export function Field({
                          label, span, isInvalid, errorText, children,
                      }: {
    label: string;
    span?: number;
    isInvalid?: boolean;
    errorText?: string;
    children: React.ReactNode;
}) {
    return (
        <FormControl gridColumn={span ? `span ${span}` : undefined} isInvalid={isInvalid}>
            <FormLabel fontSize="sm">{label}</FormLabel>
            {children}
            {isInvalid && errorText && <FormErrorMessage>{errorText}</FormErrorMessage>}
        </FormControl>
    );
}

export function CoverUploader({
                                  url, busy, label, onPick,
                              }: {
    url: string;
    busy: boolean;
    label: string;
    onPick: (file: File) => void;
}) {
    const ref = useRef<HTMLInputElement>(null);
    return (
        <Box
            as="button"
            type="button"
            onClick={() => ref.current?.click()}
            position="relative"
            h="56"
            w="40"
            borderRadius="lg"
            border="1px solid"
            borderColor="blackAlpha.200"
            bg="blackAlpha.50"
            overflow="hidden"
        >
            {busy ? (
                <Center h="full"><Spinner/></Center>
            ) : url ? (
                <Image src={url} alt="cover" h="full" w="full" objectFit="cover"/>
            ) : (
                <VStack h="full" justify="center" color="gray.500" spacing={1}>
                    <Icon as={Upload} boxSize={6}/>
                    <Text fontSize="xs" px={2} textAlign="center">{label}</Text>
                </VStack>
            )}
            <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) onPick(f);
            }}/>
        </Box>
    );
}

export function UploadChip({
                               label, done, busy, accept, onPick,
                           }: {
    label: string;
    done: boolean;
    busy: boolean;
    accept?: string;
    onPick: (file: File) => void;
}) {
    const ref = useRef<HTMLInputElement>(null);
    return (
        <Button
            variant="outline"
            borderStyle="dashed"
            justifyContent="flex-start"
            borderColor={done ? "green.300" : "blackAlpha.300"}
            bg={done ? "green.50" : undefined}
            color={done ? "green.700" : undefined}
            isDisabled={busy}
            onClick={() => ref.current?.click()}
            leftIcon={busy ? <Spinner size="sm"/> : <Icon as={done ? Check : Upload} boxSize={4}/>}
        >
            <Text noOfLines={1}>{label}</Text>
            <input ref={ref} type="file" accept={accept} hidden onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) onPick(f);
            }}/>
        </Button>
    );
}

export function GenreSelect({
                                all, value, onChange, label,
                            }: {
    all: string[];
    value: string[];
    onChange: (v: string[]) => void;
    label: string;
}) {
    const [filter, setFilter] = useState("");
    const shown = all.filter((g) => g.toLowerCase().includes(filter.toLowerCase()));
    return (
        <Box>
            {value.length > 0 && (
                <Wrap mb={2}>
                    {value.map((g) => (
                        <WrapItem key={g}>
                            <Tag colorScheme="orange" borderRadius="full">
                                <TagLabel>{g}</TagLabel>
                                <TagCloseButton onClick={() => onChange(value.filter((x) => x !== g))}/>
                            </Tag>
                        </WrapItem>
                    ))}
                </Wrap>
            )}
            <Menu closeOnSelect={false}>
                <MenuButton as={Button} size="sm" variant="outline" leftIcon={<Icon as={Plus} boxSize={4}/>}>
                    {label}
                </MenuButton>
                <MenuList maxH="320px" overflowY="auto" px={2}>
                    <Input size="sm" mb={2} placeholder="…" value={filter} onChange={(e) => setFilter(e.target.value)}/>
                    <MenuOptionGroup type="checkbox" value={value} onChange={(v) => onChange(v as string[])}>
                        {shown.map((g) => (
                            <MenuItemOption key={g} value={g}>{g}</MenuItemOption>
                        ))}
                    </MenuOptionGroup>
                </MenuList>
            </Menu>
        </Box>
    );
}